import React, { useRef, useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Upload, UploadFileInfo, UploadOnAddEvent } from "@progress/kendo-react-upload";
import { Button } from "@progress/kendo-react-buttons";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import { SvgIcon } from "@progress/kendo-react-common";
import { arrowRotateCcwIcon, cancelIcon, infoCircleIcon, xIcon } from "@progress/kendo-svg-icons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNotifications } from "@presentation/components/notifications";
import { UploadRawFileService, RawFileConflictCheck, excludeConflicts } from "@application/useCases/rawFiles/UploadRawFileService";
import { UploadConflictDialog } from "@presentation/components/inventory/components/UploadConflictDialog";
import { UploadItem, useUploadStore } from "@presentation/stores/UploadStore";
import { getUploadStatusColor, getUploadStatusLabel, formatUploadDestination } from "@presentation/components/inventory/components/uploadStatusPresentation";
import { TYPES } from "@utils/inversifyJS/types";
import { PathTypeSchema } from "@domain/schemas/BI";
import { useUploadDestinationPicker } from "@presentation/components/uploadDestination/useUploadDestinationPicker";
import { UploadDestinationField } from "@presentation/components/uploadDestination/UploadDestinationField";
import type { UploadDestination } from "@presentation/components/uploadDestination/UploadDestinationPickerDialog";
import { useProjectPermission } from "@presentation/components/helpers/usePermissionCheck";
import { getUploadErrorMessage } from "@presentation/common/ErrorHelpers";
import { ProjectActions } from "@domain/schemas/workspace";

interface UploadRawFilesDialogProperties {
  projectId: string;
  onClose: () => void;
  onUploadStarted: () => void;
  onUploadSuccess?: () => void;
  initialDestination?: Partial<UploadDestination>;
}

interface PendingUpload {
  files: File[];
  rootPath: string | undefined;
  folder: string;
  check: RawFileConflictCheck;
}

interface UploadEventGroup {
  id: string;
  rootPath: string | null | undefined;
  folder: string;
  uploads: UploadItem[];
  createdAt: number;
  percent: number;
  activeCount: number;
  completedCount: number;
  failedCount: number;
  cancelledCount: number;
}

const isFinished = (status: string) => ["completed", "failed", "cancelled"].includes(status);

const formatEventTime = (createdAt: number) =>
  new Date(createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const getEventStatus = (uploadEvent: UploadEventGroup) => {
  if (uploadEvent.activeCount > 0) {
    return { label: `${uploadEvent.percent}%`, color: "inherit" };
  }

  if (uploadEvent.failedCount === 0 && uploadEvent.cancelledCount === 0) {
    return { label: "Completed", color: "green" };
  }

  if (uploadEvent.completedCount === 0 && uploadEvent.failedCount > 0 && uploadEvent.cancelledCount === 0) {
    return { label: "Failed", color: "red" };
  }

  return uploadEvent.completedCount === 0 && uploadEvent.cancelledCount > 0 && uploadEvent.failedCount === 0
    ? { label: "Cancelled", color: "#666" }
    : { label: "Completed with issues", color: "#b26a00" };
};

const getEventSummary = (uploadEvent: UploadEventGroup) => {
  const parts: string[] = [];

  if (uploadEvent.completedCount > 0) parts.push(`${uploadEvent.completedCount} completed`);
  if (uploadEvent.failedCount > 0) parts.push(`${uploadEvent.failedCount} failed`);
  if (uploadEvent.cancelledCount > 0) parts.push(`${uploadEvent.cancelledCount} cancelled`);
  if (uploadEvent.activeCount > 0) parts.push(`${uploadEvent.activeCount} in progress`);

  return parts.join(" • ");
};

export const UploadRawFilesDialog: React.FC<UploadRawFilesDialogProperties> = ({ projectId, onClose, onUploadStarted, onUploadSuccess, initialDestination }) => {
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [resumingUpload, setResumingUpload] = useState<UploadItem | null>(null);
  const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const uploadService = useInjection<UploadRawFileService>(TYPES.UploadRawFileService);
  const { dialog: destinationDialog, pickDestination, destination, error: destinationError, isChoosing } = useUploadDestinationPicker(PathTypeSchema.Raw, projectId, initialDestination);
  const { allowed } = useProjectPermission(projectId, ProjectActions.TriggerJob);
  const [errorMessage, setErrorMessage] = useState("");
  const { pushNotification } = useNotifications();
  const { activeUploads, cancelUpload, removeUpload } = useUploadStore();
  const fileInputReference = useRef<HTMLInputElement>(null);

  const rawFileUploads = Object.values(activeUploads).filter((upload) => upload.projectId === projectId && (upload.type === "rawFile" || upload.type === undefined));

  const uploadEventGroups: Record<string, UploadItem[]> = {};
  for (const upload of rawFileUploads) {
    const eventId = upload.uploadEventId || upload.id;
    uploadEventGroups[eventId] ||= [];
    uploadEventGroups[eventId].push(upload);
  }

  const uploadEvents = Object.values(uploadEventGroups)
    .map((uploads): UploadEventGroup => {
      const loaded = uploads.reduce((sum, upload) => sum + upload.progress.loaded, 0);
      const total = uploads.reduce((sum, upload) => sum + upload.progress.total, 0);
      const activeCount = uploads.filter((upload) => upload.status === "uploading" || upload.status === "pending").length;
      const completedCount = uploads.filter((upload) => upload.status === "completed").length;
      const failedCount = uploads.filter((upload) => upload.status === "failed").length;
      const cancelledCount = uploads.filter((upload) => upload.status === "cancelled").length;

      return {
        id: uploads[0].uploadEventId || uploads[0].id,
        rootPath: uploads[0].rootPath,
        folder: uploads[0].folder,
        uploads: uploads.toSorted((a, b) => a.fileName.localeCompare(b.fileName)),
        createdAt: Math.min(...uploads.map((upload) => upload.progress.startTime)),
        percent: total > 0 ? Math.round((loaded * 100) / total) : 0,
        activeCount,
        completedCount,
        failedCount,
        cancelledCount,
      };
    })
    .toSorted((a, b) => {
      if (a.activeCount > 0 && b.activeCount === 0) return -1;
      return a.activeCount === 0 && b.activeCount > 0 ? 1 : b.createdAt - a.createdAt;
    });

  const onAdd = (event: UploadOnAddEvent) => {
    setFiles(event.affectedFiles);
  };

  const startUpload = (fileObjects: File[], rootPath: string | undefined, uploadFolder: string, check: RawFileConflictCheck) => {
    if (fileObjects.length === 0) return;

    onUploadStarted();

    void uploadService
      .uploadMultipleFiles(projectId, fileObjects, rootPath, uploadFolder, check.existingSizes)
      .then((isComplete) => {
        if (onUploadSuccess) onUploadSuccess();
        if (isComplete) onClose();
      })
      .catch((error: unknown) => {
        setErrorMessage(getUploadErrorMessage(error));
      });
  };

  const handleUpload = async () => {
    if (!allowed || isChoosing || !destination || isCheckingConflicts || files.length === 0) return;
    setErrorMessage("");

    const fileObjects = files.map((file) => file.getRawFile?.()).filter((file): file is File => file instanceof File);
    if (fileObjects.length === 0) return;

    const { rootPath, folder: uploadFolder } = destination;

    setIsCheckingConflicts(true);

    let check: RawFileConflictCheck;
    try {
      check = await uploadService.findUploadConflicts(projectId, fileObjects, rootPath, uploadFolder);
    } catch (error: unknown) {
      setErrorMessage(getUploadErrorMessage(error));
      return;
    } finally {
      setIsCheckingConflicts(false);
    }

    // Ask before destroying anything; same-name/same-size matches are not conflicts and stay silent.
    if (check.conflicts.length > 0) {
      setPendingUpload({ files: fileObjects, rootPath, folder: uploadFolder, check });
      return;
    }

    setFiles([]);
    startUpload(fileObjects, rootPath, uploadFolder, check);
  };

  const handleOverwriteAll = () => {
    if (!allowed || !pendingUpload) return;

    const { files: fileObjects, rootPath, folder: uploadFolder, check } = pendingUpload;
    setPendingUpload(null);
    setFiles([]);
    startUpload(fileObjects, rootPath, uploadFolder, check);
  };

  const handleSkipConflicting = () => {
    if (!allowed || !pendingUpload) return;

    const { files: fileObjects, rootPath, folder: uploadFolder, check } = pendingUpload;
    const remaining = excludeConflicts(fileObjects, check.conflicts);

    setPendingUpload(null);
    setFiles([]);

    if (remaining.length === 0) return;

    startUpload(remaining, rootPath, uploadFolder, check);
  };

  const handleCancelConflicts = () => {
    // Keep the current selection so the user can adjust the target folder or drop files.
    setPendingUpload(null);
  };

  const handleResumeClick = (upload: UploadItem) => {
    if (!allowed) return;
    if (uploadService.hasFileInCache(upload.id)) {
      void uploadService.resumeUpload(upload.id, upload.folder).catch((error: unknown) => setErrorMessage(getUploadErrorMessage(error)));
    } else {
      setResumingUpload(upload);
      if (fileInputReference.current) {
        fileInputReference.current.click();
      }
    }
  };

  const renderUploadActionButton = (upload: UploadItem) => {
    if (upload.status === "uploading" || upload.status === "pending") {
      return (
        <Button fillMode="flat" title="Cancel Upload" onClick={() => cancelUpload(upload.id)}>
          <SvgIcon icon={cancelIcon} />
        </Button>
      );
    }
    if (upload.status === "failed" || upload.status === "cancelled") {
      return (
        <Button fillMode="flat" title="Resume Upload" disabled={!allowed} onClick={() => handleResumeClick(upload)}>
          <SvgIcon icon={arrowRotateCcwIcon} />
        </Button>
      );
    }
    return null;
  };

  const handleFileReselect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (allowed && file && resumingUpload) {
      void uploadService.resumeUpload(file, resumingUpload.folder, resumingUpload.id).catch((error: unknown) => setErrorMessage(getUploadErrorMessage(error)));
      setResumingUpload(null);
    }
    event.target.value = "";
  };

  const toggleEvent = (eventId: string) => {
    setExpandedEventIds((current) => (current.includes(eventId) ? current.filter((id) => id !== eventId) : [...current, eventId]));
  };

  const cancelEvent = (uploadEvent: UploadEventGroup) => {
    const cancellableUploads = uploadEvent.uploads.filter((upload) => upload.status === "uploading" || upload.status === "pending");
    for (const upload of cancellableUploads) {
      cancelUpload(upload.id);
    }
  };

  const removeEvent = (uploadEvent: UploadEventGroup) => {
    for (const upload of uploadEvent.uploads) {
      removeUpload(upload.id);
    }
    setExpandedEventIds((current) => current.filter((id) => id !== uploadEvent.id));
  };

  const retryEvent = (uploadEvent: UploadEventGroup) => {
    if (!allowed) return;
    const retryableUploads = uploadEvent.uploads.filter((upload) => upload.status === "failed" || upload.status === "cancelled");

    const missingCachedFiles = retryableUploads.filter((upload) => !uploadService.hasFileInCache(upload.id));

    const cachedRetryableUploads = retryableUploads.filter((upload) => uploadService.hasFileInCache(upload.id));
    for (const upload of cachedRetryableUploads) {
      void uploadService.resumeUpload(upload.id, upload.folder).catch((error: unknown) => {
        setErrorMessage(getUploadErrorMessage(error));
      });
    }

    if (missingCachedFiles.length > 0) {
      pushNotification(
        `${missingCachedFiles.length} file${missingCachedFiles.length === 1 ? " is" : "s are"} no longer available for automatic retry. Use the file-level retry to reselect ${missingCachedFiles.length === 1 ? "it" : "them"}.`,
        "warning",
        8000,
      );
    }
  };

  const summarizeFiles = (uploads: UploadItem[]) => {
    if (uploads.length === 1) return uploads[0].fileName;
    return uploads.length === 2 ? `${uploads[0].fileName}, ${uploads[1].fileName}` : `${uploads[0].fileName} + ${uploads.length - 1} more`;
  };

  return (
    <Dialog title="Upload Raw Files" onClose={onClose} width={800}>
      <div style={{ padding: "10px" }}>
        <input type="file" ref={fileInputReference} style={{ display: "none" }} onChange={handleFileReselect} />

        <div style={{ marginBottom: "20px" }}>
          <UploadDestinationField
            destination={destination}
            onChoose={() => void pickDestination(files[0]?.name ?? (files.length > 1 ? `${files.length} files` : "Selected files"))}
            disabled={!allowed || isChoosing || isCheckingConflicts}
            error={destinationError}
          />
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Select files to upload:</label>
          <p style={{ fontSize: "0.8rem", color: "#666", marginTop: "0", marginBottom: "5px" }}>Choose a directory, select files, then start the upload.</p>
          <div
            style={{ fontSize: "0.8rem", color: "#f1c70c", marginBottom: "5px", display: "flex", alignItems: "center", gap: "5px" }}
            title="For very large files, browser-based upload might be slower or less reliable than using the CLI or dedicated tools. Please do not refresh the page during upload."
          >
            <SvgIcon icon={infoCircleIcon} />
            <span>Large files? Do not refresh page during upload. Hover for details.</span>
          </div>
          <Upload
            batch={false}
            multiple={true}
            autoUpload={false}
            disabled={!allowed || isChoosing || isCheckingConflicts}
            files={files}
            onAdd={onAdd}
            onRemove={(e) => setFiles(e.newState)}
            showActionButtons={false}
          />
          {!allowed && <p role="alert">You do not have permission to upload files in this project.</p>}
          {errorMessage && (
            <p role="alert" style={{ color: "#b91c1c", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {errorMessage}
            </p>
          )}
        </div>

        {uploadEvents.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>Upload Events:</label>
            <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #eee", padding: "10px", borderRadius: "4px" }}>
              {uploadEvents.map((uploadEvent) => {
                const isExpanded = expandedEventIds.includes(uploadEvent.id);
                const eventStatus = getEventStatus(uploadEvent);
                const eventSummary = getEventSummary(uploadEvent);

                return (
                  <div key={uploadEvent.id} style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: "1px solid #f5f5f5" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: "0.85rem", marginBottom: "5px" }}>
                      <div style={{ flex: 1, marginRight: "10px", minWidth: 0 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: "bold" }} title={summarizeFiles(uploadEvent.uploads)}>
                          {uploadEvent.uploads.length === 1 ? uploadEvent.uploads[0].fileName : `${uploadEvent.uploads.length} files`}
                        </div>
                        <div
                          style={{ fontSize: "0.75rem", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={formatUploadDestination(uploadEvent.rootPath, uploadEvent.folder)}
                        >
                          Folder: {formatUploadDestination(uploadEvent.rootPath, uploadEvent.folder)} • Started {formatEventTime(uploadEvent.createdAt)}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={summarizeFiles(uploadEvent.uploads)}>
                          {summarizeFiles(uploadEvent.uploads)}
                        </div>
                        {eventSummary && <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "2px" }}>{eventSummary}</div>}
                        {uploadEvent.uploads
                          .filter((upload) => upload.status === "failed")
                          .map((upload) => (
                            <p key={upload.id} role="alert" style={{ color: "#b91c1c", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                              {upload.fileName}: {upload.error || "Upload failed."}
                            </p>
                          ))}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span style={{ color: eventStatus.color, fontWeight: "bold" }}>{eventStatus.label}</span>

                        <Button fillMode="flat" title={isExpanded ? "Hide files" : "Show files"} onClick={() => toggleEvent(uploadEvent.id)}>
                          {isExpanded ? "Hide files" : "Show files"}
                        </Button>

                        {uploadEvent.activeCount > 0 ? (
                          <Button fillMode="flat" title="Cancel Upload Event" onClick={() => cancelEvent(uploadEvent)}>
                            <SvgIcon icon={cancelIcon} />
                          </Button>
                        ) : (
                          <>
                            {(uploadEvent.failedCount > 0 || uploadEvent.cancelledCount > 0) && (
                              <Button fillMode="flat" title="Retry failed uploads" onClick={() => retryEvent(uploadEvent)}>
                                Retry
                              </Button>
                            )}

                            <Button fillMode="flat" title="Remove from list" onClick={() => removeEvent(uploadEvent)}>
                              <SvgIcon icon={xIcon} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <ProgressBar value={uploadEvent.percent} labelVisible={false} style={{ height: "5px", borderRadius: "3px", marginTop: "2px", marginBottom: isExpanded ? "8px" : "2px" }} />

                    {isExpanded && (
                      <div style={{ padding: "8px", background: "#fafafa", borderRadius: "4px" }}>
                        {uploadEvent.uploads.map((upload) => (
                          <div key={upload.id} style={{ marginBottom: "6px", paddingBottom: "6px", borderBottom: "1px solid #f0f0f0" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: "0.8rem", marginBottom: "4px" }}>
                              <div style={{ flex: 1, marginRight: "10px", minWidth: 0 }}>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }} title={upload.fileName}>
                                  {upload.fileName}
                                </div>
                                {upload.status === "failed" && upload.error && (
                                  <div style={{ fontSize: "0.75rem", color: "red", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={upload.error}>
                                    Error: {upload.error}
                                  </div>
                                )}
                              </div>

                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <span style={{ color: getUploadStatusColor(upload.status), fontWeight: "bold" }}>{getUploadStatusLabel(upload)}</span>

                                {renderUploadActionButton(upload)}

                                {isFinished(upload.status) && (
                                  <Button fillMode="flat" title="Remove from list" onClick={() => removeUpload(upload.id)}>
                                    <SvgIcon icon={xIcon} />
                                  </Button>
                                )}
                              </div>
                            </div>

                            <ProgressBar value={upload.progress.percent} labelVisible={false} style={{ height: "4px", borderRadius: "3px" }} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Button onClick={onClose}>Close</Button>
          <Button themeColor="primary" onClick={handleUpload} disabled={!allowed || !destination || files.length === 0 || isCheckingConflicts || isChoosing}>
            {isCheckingConflicts ? "Checking..." : "Start Upload"}
          </Button>
        </div>
      </div>

      {pendingUpload && (
        <UploadConflictDialog
          conflicts={pendingUpload.check.conflicts}
          nonConflictingCount={pendingUpload.files.length - pendingUpload.check.conflicts.length}
          folder={pendingUpload.folder}
          onOverwriteAll={handleOverwriteAll}
          onSkipConflicting={handleSkipConflicting}
          onCancel={handleCancelConflicts}
        />
      )}
      {destinationDialog}
    </Dialog>
  );
};
